/**
 * Whole-hole terrain elevation for the .pocketgolf v2 format.
 *
 * A v2 hole may carry an `elevation` array alongside its `grid`: one string per row, one character per
 * cell, `0`–`9`. Unlike `heights`, which is read only on the putting surface, **every** cell is read —
 * this is the ground the whole hole stands on.
 *
 * ## Why this is not just `heights` with a wider reach
 *
 * The two fields mean different things at the same coordinates, and conflating them would silently
 * re-cut every course already authored. `heights` deliberately has no value off the green, so the drop
 * from a green down to the ground around it is not a slope a putt can feel — a green sitting at 3 in a
 * matrix padded with zeros is *correct* under `heights` and a three-step cliff under `elevation`.
 *
 * So they are separate fields. A hole may declare either; where it declares both, the game reads
 * `elevation` and derives the green's contour from it, which is what keeps the approach and the putt
 * agreeing about how high the green stands.
 *
 * ## What a step is worth
 *
 * Five metres, at the game's ten-metres-a-tile scale. A shot loses about 0.3 tiles of carry per step it
 * climbs, a ball runs further downhill than up, and a tree's canopy is measured from the ground it
 * stands on — so a treeline on a rise really is taller: dense trees eight steps up are too tall for any
 * club in the bag to clear.
 *
 * The game's copy of this arithmetic is Sources/PocketGolfCore/TerrainElevation.swift. The two must
 * agree, or the shading and arrows drawn here describe a hole the ball does not play. Keep them in sync.
 */

import { DEFAULT_WIDTH, gridWidth } from './grid.js';
import { clampHeight, MAX_HEIGHT, MIN_HEIGHT, slopeGrade } from './contour.js';

export { MAX_HEIGHT, MIN_HEIGHT, slopeGrade };

/** The step a new terrain matrix starts at: the middle, so an author can go up *or* down from it. */
export const DEFAULT_STEP = 4;

/** Metres per elevation step. Mirrors Physics.elevationStepTiles (0.5 tiles) at 10 m/tile. */
export const STEP_METRES = 5;

function parseStep(char) {
	const value = parseInt(char, 10);
	return Number.isFinite(value) ? value : null;
}

/**
 * A terrain matrix shaped to the given grid.
 *
 * Missing characters take `fill` rather than 0. That is the one place a forgiving reader can do real
 * damage here: a hole authored in the 4–6 band whose rows ran a few characters short would otherwise
 * drop off a four-step cliff at the ragged edge. The game's parser pads with the matrix's own lowest
 * value for exactly the same reason.
 */
export function normaliseElevation(elevation, grid, fill = DEFAULT_STEP) {
	const source = Array.isArray(elevation) ? elevation : [];
	const padding = clampHeight(fill);

	const width = gridWidth(grid);
	return grid.map((_, y) => {
		const row = typeof source[y] === 'string' ? source[y] : '';
		let out = '';
		for (let x = 0; x < width; x++) {
			const parsed = parseStep(row.charAt(x));
			out += String(parsed === null ? padding : clampHeight(parsed));
		}
		return out;
	});
}

/** A terrain matrix at one uniform step, shaped to a grid. */
export function flatElevation(rows, step = DEFAULT_STEP, width = DEFAULT_WIDTH) {
	return Array.from({ length: rows }, () => String(clampHeight(step)).repeat(width));
}

/**
 * The step at a cell.
 *
 * Clamped to the grid rather than returning null. Every cell of a hole stands somewhere, and the sane
 * reading of "how high is it just past the edge" is "the same as at the edge" — which is also what the
 * game does, so a gradient computed at the boundary matches.
 */
export function stepAt(elevation, x, y) {
	if (!Array.isArray(elevation) || elevation.length === 0) return 0;
	const row = elevation[Math.max(0, Math.min(y, elevation.length - 1))] || '';
	const parsed = parseStep(row.charAt(Math.max(0, Math.min(x, row.length - 1))));
	return parsed === null ? 0 : clampHeight(parsed);
}

/**
 * Which way the ground falls away under a cell, and how steeply.
 *
 * Central differences pointing downhill, the same convention `slopeAt` in contour.js uses so an author
 * reads a hillside and a green the same way. The difference is at the edges: terrain clamps to the
 * boundary cell rather than substituting the centre, because a hole does not stop existing at column 19
 * — it simply is not drawn any further.
 *
 * `dx`/`dy` are in height steps per tile. `angle` is the game's aim convention: degrees, 0 = north,
 * increasing clockwise.
 */
export function terrainSlopeAt(elevation, x, y) {
	// The `+ 0` turns negative zero back into zero. Negating a difference of nothing gives `-0` in
	// JavaScript, which compares equal to 0 but prints as "-0" — so a perfectly level hole would show a
	// gradient of "-0.00" in a tooltip and read as a rounding bug.
	const dx = -(stepAt(elevation, x + 1, y) - stepAt(elevation, x - 1, y)) / 2 + 0;
	const dy = -(stepAt(elevation, x, y + 1) - stepAt(elevation, x, y - 1)) / 2 + 0;
	const steepness = Math.sqrt(dx * dx + dy * dy);
	let angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
	if (angle < 0) angle += 360;

	return { dx, dy, steepness, angle };
}

/** The lowest and highest step anywhere on the hole. */
export function elevationRange(elevation) {
	if (!Array.isArray(elevation) || elevation.length === 0) return null;

	let low = Infinity;
	let high = -Infinity;
	const width = gridWidth(elevation);
	for (let y = 0; y < elevation.length; y++) {
		for (let x = 0; x < width; x++) {
			const value = stepAt(elevation, x, y);
			low = Math.min(low, value);
			high = Math.max(high, value);
		}
	}
	return low <= high ? { low, high } : null;
}

/**
 * Whether this hole has terrain worth exporting.
 *
 * A hole at one uniform step plays exactly flat and the game declares no elevation for it, so a matrix
 * of identical numbers is dropped rather than written out. That keeps a file from claiming a feature it
 * does not use, and keeps the diff of a course nobody sculpted completely empty.
 */
export function hasElevation(elevation) {
	const range = elevationRange(elevation);
	return range !== null && range.low < range.high;
}

/**
 * How far a shot to `to` plays uphill from `from`, in metres.
 *
 * The number an author is really designing against — "this approach plays fifteen metres uphill" — and
 * the one the game puts in its CLIMB readout. Positive is uphill.
 */
export function climbMetres(elevation, from, to) {
	const steps = stepAt(elevation, to.x, to.y) - stepAt(elevation, from.x, from.y);
	// One step is 0.5 tiles and a tile is 10 m, so a step is 5 m. Both constants live in Physics:
	// elevationStepTiles and metresPerTile.
	return steps * STEP_METRES;
}
